import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Send, Trash2, Edit3, Loader2, Settings, RefreshCw, CheckCircle2, AlertCircle, Clock, X, Eye } from "lucide-react";
import api from "@/lib/api";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Admin "Mails" inbox — lists every email the platform has queued for the
 * admin to review + send via their own SMTP mailbox (configured under
 * /admin/mail-settings). Three tabs: Drafts (pending review), Sent
 * (history), Failed (error log + retry).
 */
export default function AdminMails() {
    const [mails, setMails] = useState([]);
    const [counts, setCounts] = useState({ draft: 0, sent: 0, failed: 0 });
    const [tab, setTab] = useState("draft");
    const [loading, setLoading] = useState(true);
    const [preview, setPreview] = useState(null); // mail object opened in side-modal
    const [sending, setSending] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    // Optional personal intro the admin can type before sending — gets
    // injected at the very top of the templated mail body server-side.
    const [personalIntro, setPersonalIntro] = useState("");
    // Cached mail settings → used to show the admin which CC address will
    // automatically receive a copy when the mail is sent.
    const [mailSettings, setMailSettings] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/mails?status=${tab}`);
            setMails(data.mails || []);
            setCounts(data.counts || { draft: 0, sent: 0, failed: 0 });
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kon mails niet ophalen");
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    // Load mail settings once so we can preview the auto-CC.
    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/admin/mail-settings");
                if (data?.configured) setMailSettings(data);
            } catch (_) {
                /* silent — settings page surfaces the error already */
            }
        })();
    }, []);

    const openPreview = async (mail) => {
        try {
            const { data } = await api.get(`/admin/mails/${mail.id}`);
            setPreview(data);
            setPersonalIntro(""); // reset intro when opening a different mail
        } catch (e) {
            toast.error(e.response?.data?.detail || "Kon mail niet laden");
        }
    };

    const sendMail = async (mailId) => {
        setSending(true);
        try {
            const { data } = await api.post(`/admin/mails/${mailId}/send`, {
                personal_intro: personalIntro.trim() || null,
            });
            if (data.ok) {
                toast.success("Mail verstuurd");
                setPreview(null);
                setPersonalIntro("");
                load();
            } else {
                toast.error(`Versturen mislukt: ${data.error || "onbekende fout"}`);
                load();
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Versturen mislukt");
        } finally {
            setSending(false);
        }
    };

    const performDelete = async () => {
        if (!deleteTarget) return;
        try {
            await api.delete(`/admin/mails/${deleteTarget.id}`);
            toast.success("Mail verwijderd");
            setDeleteTarget(null);
            if (preview?.id === deleteTarget.id) setPreview(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Verwijderen mislukt");
        }
    };

    const tabs = [
        { key: "draft", label: "Concepten", icon: Clock, color: "text-[#E5A93D]" },
        { key: "sent", label: "Verzonden", icon: CheckCircle2, color: "text-emerald-600" },
        { key: "failed", label: "Mislukt", icon: AlertCircle, color: "text-rose-600" },
    ];

    return (
        <div className="min-h-screen bg-zinc-50 text-zinc-950" data-testid="admin-mails-page">
            <div className="max-w-[1300px] mx-auto px-6 lg:px-10 py-12">
                <div className="flex items-end justify-between gap-6 mb-10">
                    <div>
                        <p className="text-[11px] uppercase tracking-[0.18em] text-[#E5A93D] mb-3 inline-flex items-center gap-2">
                            <Mail className="h-3.5 w-3.5" /> Admin · Mailbox
                        </p>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">Mails.</h1>
                        <p className="mt-3 text-sm text-zinc-500 max-w-2xl">
                            Het platform bereidt automatisch mails voor (registratie, credit-aankoop, video-oplevering). Jij reviewt en verstuurt ze vanuit je eigen mailbox.
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={load}
                            data-testid="mails-refresh-btn"
                            className="h-10 w-10 inline-flex items-center justify-center rounded-sm border border-zinc-200 text-zinc-600 hover:text-zinc-950 hover:bg-zinc-100"
                            title="Vernieuwen"
                        >
                            <RefreshCw className="h-4 w-4" />
                        </button>
                        <Link
                            to="/admin/leads"
                            data-testid="mails-leads-link"
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-zinc-200 text-sm text-zinc-800 hover:text-zinc-950 hover:bg-zinc-100"
                        >
                            <Send className="h-4 w-4" /> Leads
                        </Link>
                        <Link
                            to="/admin/mail-templates"
                            data-testid="mails-templates-link"
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-zinc-200 text-sm text-zinc-800 hover:text-zinc-950 hover:bg-zinc-100"
                        >
                            <Settings className="h-4 w-4" /> Mail-templates
                        </Link>
                        <Link
                            to="/admin/mail-settings"
                            data-testid="mails-settings-link"
                            className="inline-flex items-center gap-2 h-10 px-4 rounded-sm border border-zinc-200 text-sm text-zinc-800 hover:text-zinc-950 hover:bg-zinc-100"
                        >
                            <Settings className="h-4 w-4" /> Mailbox instellen
                        </Link>
                    </div>
                </div>

                {/* Tabs */}
                <div className="flex gap-1 border-b border-zinc-200 mb-6">
                    {tabs.map((t) => {
                        const active = tab === t.key;
                        const Icon = t.icon;
                        return (
                            <button
                                key={t.key}
                                onClick={() => setTab(t.key)}
                                data-testid={`mails-tab-${t.key}`}
                                className={`inline-flex items-center gap-2 px-4 h-10 text-xs uppercase tracking-wider transition border-b-2 -mb-px ${active ? "border-[#E5A93D] text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-600"}`}
                            >
                                <Icon className={`h-3.5 w-3.5 ${active ? t.color : ""}`} />
                                {t.label}
                                <span className={`ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-sm text-[10px] tabular-nums ${active ? "bg-zinc-100 text-zinc-950" : "bg-zinc-100 text-zinc-500"}`}>
                                    {counts[t.key] || 0}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* List */}
                {loading ? (
                    <div className="flex items-center gap-3 text-sm text-zinc-500 py-10">
                        <Loader2 className="h-4 w-4 animate-spin" /> Mails ophalen…
                    </div>
                ) : mails.length === 0 ? (
                    <div className="bg-white border border-zinc-200 rounded-sm p-12 text-center">
                        <Mail className="h-10 w-10 mx-auto text-zinc-400 mb-4" />
                        <p className="text-sm text-zinc-600">
                            {tab === "draft" ? "Geen concepten" : tab === "sent" ? "Nog niets verstuurd" : "Geen mislukte mails"}
                        </p>
                    </div>
                ) : (
                    <div className="bg-white border border-zinc-200 rounded-sm overflow-hidden">
                        {mails.map((m) => (
                            <div
                                key={m.id}
                                data-testid={`mail-row-${m.id}`}
                                onClick={() => openPreview(m)}
                                className="grid grid-cols-[140px_1.2fr_2fr_140px] gap-4 px-6 py-3 border-b border-zinc-100 last:border-b-0 hover:bg-zinc-100 cursor-pointer transition items-center"
                            >
                                <div className="text-[11px] uppercase tracking-wider">
                                    <span className={
                                        m.kind === "welcome" ? "text-[#E5A93D]"
                                        : m.kind === "purchase" ? "text-emerald-600"
                                        : m.kind === "delivery" ? "text-[#7C3AED]"
                                        : "text-zinc-500"
                                    }>{m.kind}</span>
                                </div>
                                <div className="text-sm">
                                    <p className="text-zinc-950 truncate">{m.to_name || m.to_email}</p>
                                    <p className="text-[11px] text-zinc-500 truncate">{m.to_email}</p>
                                </div>
                                <div className="text-sm text-zinc-600 truncate">{m.subject}</div>
                                <div className="text-[11px] text-zinc-500 tabular-nums text-right">
                                    {new Date(m.created_at).toLocaleDateString("nl-BE", { day: "2-digit", month: "short" })} ·{" "}
                                    {new Date(m.created_at).toLocaleTimeString("nl-BE", { hour: "2-digit", minute: "2-digit" })}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Preview drawer */}
            {preview && (
                <div className="fixed inset-0 z-[90] flex bg-black/70 backdrop-blur-sm" onClick={() => setPreview(null)} data-testid="mail-preview-modal">
                    <div className="ml-auto w-full max-w-[760px] h-full bg-white border-l border-zinc-200 flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-zinc-200">
                            <div className="min-w-0 flex-1">
                                <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{preview.kind} · naar {preview.to_email}</p>
                                <h3 className="font-display text-xl font-bold tracking-tight" data-testid="mail-preview-subject">{preview.subject}</h3>
                                {preview.error && (
                                    <p className="mt-2 text-xs text-rose-700 bg-rose-500/10 border border-rose-500/30 rounded-sm px-2 py-1.5">
                                        Fout: {preview.error}
                                    </p>
                                )}
                            </div>
                            <button onClick={() => setPreview(null)} className="h-8 w-8 rounded-sm hover:bg-zinc-100 inline-flex items-center justify-center" data-testid="mail-preview-close">
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-y-auto bg-zinc-50 p-4 space-y-3">
                            {/* Personal intro — only editable on drafts; for sent
                                mails we just show the saved intro (if any) for
                                audit/reference. */}
                            {preview.status !== "sent" ? (
                                <div className="bg-white border border-[#E5A93D]/30 rounded-sm p-3">
                                    <label className="block text-[10px] uppercase tracking-[0.18em] text-[#E5A93D] mb-2 font-bold">
                                        Persoonlijke intro <span className="text-zinc-500 normal-case tracking-normal font-normal">(optioneel — verschijnt bovenaan de mail)</span>
                                    </label>
                                    <textarea
                                        value={personalIntro}
                                        onChange={(e) => setPersonalIntro(e.target.value)}
                                        placeholder="Bv. 'Dag Marc, leuk dat u inschreef — hier alvast uw 10 gratis credits…'"
                                        rows={3}
                                        data-testid="mail-personal-intro"
                                        className="w-full bg-white border border-zinc-200 focus:border-[#E5A93D] focus:ring-1 focus:ring-[#E5A93D]/30 outline-none rounded-sm text-sm text-zinc-900 p-2.5 resize-none placeholder:text-zinc-400"
                                    />
                                    <p className="mt-1.5 text-[11px] text-zinc-500">
                                        Lege regels worden alinea&apos;s. De rest van de mail (logo, knoppen, footer) blijft ongewijzigd.
                                    </p>
                                    {mailSettings && (
                                        <p className="mt-2 text-[11px] text-zinc-500 inline-flex items-center gap-1.5">
                                            <span className="text-[9px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded-sm bg-emerald-500/15 border border-emerald-500/30 text-emerald-700 font-bold">CC</span>
                                            <span className="text-zinc-500">
                                                {(mailSettings.cc_email || mailSettings.smtp_user || "—").split(",").map(s => s.trim()).filter(Boolean).join(", ")}
                                                <span className="text-zinc-400 ml-1">(automatisch — wijzig via mailbox-instellingen)</span>
                                            </span>
                                        </p>
                                    )}
                                </div>
                            ) : preview.personal_intro_sent ? (
                                <div className="bg-white border border-emerald-500/30 rounded-sm p-3">
                                    <p className="text-[10px] uppercase tracking-[0.18em] text-emerald-600 mb-2 font-bold">
                                        Verstuurde intro
                                    </p>
                                    <p className="text-sm text-zinc-800 whitespace-pre-line">{preview.personal_intro_sent}</p>
                                </div>
                            ) : null}
                            <iframe
                                title="email-preview"
                                srcDoc={preview.body_html}
                                className="w-full h-full min-h-[600px] bg-white rounded-sm border border-zinc-200"
                                sandbox=""
                                data-testid="mail-preview-iframe"
                            />
                        </div>
                        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-zinc-200">
                            <button
                                onClick={() => setDeleteTarget(preview)}
                                data-testid="mail-preview-delete"
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-rose-500/30 text-rose-700 text-xs font-medium hover:bg-rose-500/10"
                            >
                                <Trash2 className="h-3.5 w-3.5" /> Verwijder
                            </button>
                            {preview.status === "sent" ? (
                                <span className="inline-flex items-center gap-2 text-xs text-emerald-600">
                                    <CheckCircle2 className="h-4 w-4" />
                                    Verstuurd op {new Date(preview.sent_at).toLocaleString("nl-BE")}
                                </span>
                            ) : (
                                <button
                                    onClick={() => sendMail(preview.id)}
                                    disabled={sending}
                                    data-testid="mail-preview-send"
                                    className="inline-flex items-center gap-2 h-10 px-5 rounded-sm bg-[#E5A93D] hover:bg-[#C8922C] text-black font-bold text-sm transition"
                                >
                                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                    Verstuur naar {preview.to_email}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Mail verwijderen?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Je verwijdert de mail "{deleteTarget?.subject}" naar {deleteTarget?.to_email}. Deze actie kan niet ongedaan gemaakt worden.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuleren</AlertDialogCancel>
                        <AlertDialogAction onClick={(e) => { e.preventDefault(); performDelete(); }} className="bg-rose-600 hover:bg-rose-500">
                            Verwijder
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
